import { AddIcon } from '@chakra-ui/icons'
import {
  Accordion,
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Input,
  Stack,
  useToast,
} from '@chakra-ui/react'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import {
  addDoc,
  doc,
  DocumentData,
  getDoc,
  QueryDocumentSnapshot,
  updateDoc,
} from 'firebase/firestore'
import { chain } from 'lodash'
import { useEffect, useState } from 'react'
import { FaEye, FaEyeSlash } from 'react-icons/fa'
import { useParams } from 'react-router-dom'
import labsCollection, { useLabs } from '../../../data/labsCollection'
import useHandleError from '../../../hooks/useHandleError'
import { Lab } from '../../../types/bloodWork'
import randomId from '../../../utils/randomId'
import NotFound from '../../NotFound'
import AdminBloodWork from '.'
import useConfigList from '../../../hooks/useConfigList'
import CopyLabForm from './CopyLabForm'
import PanelAccordionItem from './components/PanelAccordionItem'
import useLabEditor from './hooks/useLabEditor'
import useLabDragDrop from './hooks/useLabDragDrop'
import {
  transformLabForEdit,
  transformLabForSave,
} from './utils/labTransformers'

export default () => {
  const { editingId } = useParams<{ editingId: string }>()
  const [lab, setLab] = useState<Lab>({
    title: '',
    panels: [],
    archived: false,
  })
  const [saving, setSaving] = useState(false)
  const [labs] = useLabs({})
  const handleError = useHandleError()
  const toast = useToast()
  const [notFound, setNotFound] = useState(false)
  const bloodWorkUnits = useConfigList('measurement-units')

  // Initialize custom hooks
  const labEditorActions = useLabEditor(setLab)
  const { handleDragEnd } = useLabDragDrop(lab, setLab)

  const setLabFromCopy = (copyLab: Lab) => {
    setLab(transformLabForEdit(copyLab))
  }

  const copyButtonFunc = (
    copyLabId: string,
    labs: QueryDocumentSnapshot<Lab, DocumentData>[]
  ): boolean => {
    const copyLab = labs.find((doc) => doc.id === copyLabId)?.data()
    if (
      copyLab &&
      window.confirm(
        `Warning: This will overwrite this entire form and replace it with the entirety of lab "${copyLab.title}". Proceed?`
      )
    ) {
      setLabFromCopy(copyLab)
      return true
    }
    return false
  }

  useEffect(() => {
    if (!editingId) return

    getDoc(doc(labsCollection, editingId)).then((snap) => {
      const lab = snap.data()
      if (lab) {
        setLabFromCopy(lab)
      } else {
        setNotFound(true)
      }
    })
  }, [editingId])

  if (notFound) {
    return <NotFound />
  }

  const saveLab = async () => {
    setSaving(true)
    try {
      const newLab = transformLabForSave(lab)

      if (editingId) {
        await updateDoc(doc(labsCollection, editingId), newLab)
      } else {
        labs?.forEach((labTemplate) => {
          if (
            labTemplate.data().title.toLocaleLowerCase() ===
            newLab.title.toLocaleLowerCase()
          ) {
            throw new Error('Lab name already exists!')
          }
        })

        await addDoc(labsCollection, newLab)
        setLab({
          panels: [],
          title: '',
          archived: false,
        })
      }
      toast({ status: 'success', description: 'Lab saved!' })
    } catch (e) {
      handleError(e)
    } finally {
      setSaving(false)
    }
  }

  const getBloodWorkUnitOptions = () => {
    return chain(bloodWorkUnits)
      .map((text, unit) => {
        const textLowerCase = text.toLowerCase()
        return {
          text,
          unit,
          textLowerCase,
        }
      })
      .orderBy('textLowerCase', 'asc')
      .map(({ text, unit }) => {
        return (
          <option value={unit} key={unit}>
            {text}
          </option>
        )
      })
      .value()
  }

  const bloodWorkUnitOptions = getBloodWorkUnitOptions()

  return (
    <Stack spacing={5}>
      <AdminBloodWork />
      {!editingId && (
        <>
          <CopyLabForm labs={labs} copyButtonFunc={copyButtonFunc} />
          <Divider />
        </>
      )}
      <Heading>{editingId ? 'Edit' : 'Create'} Lab</Heading>
      <Divider />
      <Box>
        <Button
          size='lg'
          isDisabled={saving}
          isLoading={saving}
          onClick={saveLab}
        >
          Save
        </Button>
      </Box>

      <Stack spacing={3}>
        <FormControl maxW='md'>
          <FormLabel>Lab Title</FormLabel>
          <Input
            placeholder='e.g. Quest, LabCorp, etc...'
            value={lab.title}
            onChange={(e) => {
              setLab({
                ...lab,
                title: e.target.value,
              })
            }}
          />
        </FormControl>
        <HStack spacing={2}>
          <Button
            size='sm'
            variant='outline'
            leftIcon={<AddIcon />}
            onClick={() => {
              setLab({
                ...lab,
                panels: [
                  ...lab.panels,
                  {
                    id: randomId(),
                    title: '',
                    items: [],
                  },
                ],
              })
            }}
          >
            Add Panel
          </Button>
          <Button
            size='sm'
            variant='outline'
            colorScheme={lab.archived ? 'gray' : 'red'}
            leftIcon={lab.archived ? <FaEye /> : <FaEyeSlash />}
            onClick={() => {
              setLab({
                ...lab,
                archived: !lab.archived,
              })
            }}
          >
            {lab.archived ? 'Unarchive Lab' : 'Archive Lab'}
          </Button>
        </HStack>
      </Stack>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId='panels' type='PANEL'>
          {(provided) => (
            <Accordion
              allowMultiple
              defaultIndex={[]}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {lab.panels.map((panel, index) => (
                <Draggable key={panel.id} draggableId={panel.id} index={index}>
                  {(provided, snapshot) => (
                    <PanelAccordionItem
                      panel={panel}
                      panelIndex={index}
                      bloodWorkUnitOptions={bloodWorkUnitOptions}
                      dragProvided={provided}
                      dragSnapshot={snapshot}
                      {...labEditorActions}
                    />
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Accordion>
          )}
        </Droppable>
      </DragDropContext>
      <Box>
        <Button
          size='lg'
          isDisabled={saving}
          isLoading={saving}
          onClick={saveLab}
        >
          Save
        </Button>
      </Box>
    </Stack>
  )
}
